import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Input } from "@/components/ui/input.tsx";
import { UserDetailsCard } from "@/components/users/user-details/user-details-card.tsx";
import { User } from "@/lib/api/user.ts";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { useUpdateUser } from "@/lib/hooks/user/use-update-user.ts";
import { REDACTED_LABEL } from "@/lib/redacted-display";

interface UserParentDetailsProps {
  user: User;
}

export function UserParentDetails({ user }: UserParentDetailsProps) {
  const ability = useAbility();
  const canUpdateUsers =
    hasPrivilegedAccessFromAbility(ability) && ability.can("update", "users");
  const redactedFields = new Set(user.redacted_fields ?? []);
  const guardianNameRedacted = redactedFields.has("guardian_name");
  const emergencyPhoneRedacted = redactedFields.has("emergency_phone");
  const emailRedacted = redactedFields.has("email");
  const { handleUpdate, form, fieldState } = useUpdateUser({
    userId: user.id,
    guardianName: user.guardian_name ?? undefined,
    emergencyPhone: user.emergency_phone || "",
    email: user.email || "",

    profile: {
      metadata: {
        parentConsent: user.profile?.metadata?.parentConsent,
        parentOptOutReason: user.profile?.metadata?.parentOptOutReason,
      },
    },
  });

  return (
    <UserDetailsCard>
      <Form {...form}>
        <form className="flex flex-col gap-4">
          <FormField
            disabled={
              !canUpdateUsers || fieldState.guardianName || guardianNameRedacted
            }
            control={form.control}
            name="guardianName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      guardianNameRedacted ? REDACTED_LABEL : "Enter parent name"
                    }
                    {...field}
                    value={guardianNameRedacted ? "" : (field.value ?? "")}
                    onBlur={() => {
                      if (guardianNameRedacted) return;
                      handleUpdate("guardianName", field.value);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="emergencyPhone"
            disabled={
              !canUpdateUsers ||
              fieldState.emergencyPhone ||
              emergencyPhoneRedacted
            }
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Phone number</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      emergencyPhoneRedacted ? REDACTED_LABEL : "Enter phone"
                    }
                    inputMode="tel"
                    {...field}
                    value={emergencyPhoneRedacted ? "" : (field.value ?? "")}
                    onBlur={() => {
                      if (emergencyPhoneRedacted) return;
                      handleUpdate("emergencyPhone", field.value);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            disabled
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder={emailRedacted ? REDACTED_LABEL : "Enter email"}
                    inputMode="email"
                    {...field}
                    value={emailRedacted ? "" : (field.value ?? "")}
                    onBlur={() => {
                      handleUpdate("email", field.value);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="profile.metadata.parentConsent"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">
                  Parent Consent (EDIT WITH CARE)
                </FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    const currentMetadata = form.getValues("profile.metadata");
                    handleUpdate("profile", {
                      metadata: {
                        ...currentMetadata,
                        parentConsent: value as "Yes" | "No",
                        parentOptOutReason: form.getValues(
                          "profile.metadata.parentOptOutReason",
                        ),
                      },
                    });
                  }}
                  value={field.value}
                  disabled={!canUpdateUsers}
                >
                  <FormControl>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select consent status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="profile.metadata.parentOptOutReason"
            disabled={true}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-neutral-500">
                  Opt-out Reason
                </FormLabel>
                <FormControl>
                  <Input placeholder="No opt-out reason given" {...field} />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </UserDetailsCard>
  );
}
